import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { Button, Input, Label } from "@/components/ui";
import { FileDropzone } from "@/components/common/FileDropzone";
import { FILE_ACCEPT } from "@/constants/fileAccept";
import {
  formatFileSizeLimit,
  validateFileWithPreset,
} from "@/lib/fileValidation";
import { formatDateTime } from "@/lib/datetime";
import { formatMoneyInput } from "@/lib/money";
import type {
  ReplaceSupplierInvoiceInput,
  SettlementLineRow,
} from "@/services/adminSettlementService";
import { SettlementStepCard, type StepCardProps } from "../SettlementStepCard";
import {
  checkSupplierInvoiceTotal,
  registeredInvoicesFromLines,
  type RegisteredSupplierInvoice,
  type SupplierInvoiceTotalError,
} from "./supplierInvoiceRules";

type InvoiceFieldValues = {
  number: string;
  date: string;
  file: File | null;
  note: string;
};

const TOTAL_ERROR_KEYS: Record<SupplierInvoiceTotalError, string> = {
  required: "settlements.detail.steps.supplierTotalRequired",
  invalid: "settlements.detail.steps.supplierTotalInvalid",
  mismatch: "settlements.detail.steps.supplierTotalMismatch",
};

function requireNumberAndFile(
  values: InvoiceFieldValues,
  t: (key: string) => string,
): boolean {
  if (!values.number.trim()) {
    toast.error(t("settlements.detail.steps.supplierNumberRequired"));
    return false;
  }
  if (!values.file) {
    toast.error(t("settlements.detail.steps.supplierFileRequired"));
    return false;
  }
  return true;
}

function SupplierInvoiceFields({
  idPrefix,
  values,
  disabled,
  onChange,
  afterDate,
}: {
  idPrefix: string;
  values: InvoiceFieldValues;
  disabled: boolean;
  onChange: (patch: Partial<InvoiceFieldValues>) => void;
  afterDate?: ReactNode;
}) {
  const { t } = useTranslation("payments");

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label
            htmlFor={`${idPrefix}-number`}
            size="sm"
            spacing="tight"
          >
            {t("settlements.detail.steps.supplierNumberLabel")}
          </Label>
          <Input
            id={`${idPrefix}-number`}
            value={values.number}
            onChange={(e) => onChange({ number: e.target.value })}
            disabled={disabled}
            placeholder={t(
              "settlements.detail.steps.supplierNumberPlaceholder",
            )}
          />
        </div>
        <div>
          <Label
            htmlFor={`${idPrefix}-date`}
            size="sm"
            spacing="tight"
          >
            {t("settlements.detail.steps.supplierDateLabel")}
          </Label>
          <Input
            id={`${idPrefix}-date`}
            type="date"
            value={values.date}
            onChange={(e) => onChange({ date: e.target.value })}
            disabled={disabled}
          />
        </div>
      </div>
      {afterDate}
      <div>
        <Label
          htmlFor={`${idPrefix}-file`}
          size="sm"
          spacing="tight"
        >
          {t("settlements.detail.steps.supplierFileLabel")}
        </Label>
        <FileDropzone
          id={`${idPrefix}-file`}
          accept={FILE_ACCEPT.pdfAndImage}
          disabled={disabled}
          title={
            values.file?.name ?? t("settlements.detail.steps.supplierFileLabel")
          }
          className="bg-surface-0 mt-1 w-full rounded-lg py-4"
          onFilesSelected={(files) => {
            const selected = files[0];
            if (!selected) {
              onChange({ file: null });
              return;
            }
            const result = validateFileWithPreset(selected, "receipt");
            if (!result.ok) {
              if (result.error.code === "too_large") {
                toast.error(
                  t("settlements.detail.steps.supplierFileTooLarge", {
                    max: formatFileSizeLimit(result.error.maxSize),
                  }),
                );
              } else {
                toast.error(
                  t("settlements.detail.steps.supplierFileUnsupported"),
                );
              }
              return;
            }
            onChange({ file: selected });
          }}
        />
      </div>
      <Input
        value={values.note}
        onChange={(e) => onChange({ note: e.target.value })}
        disabled={disabled}
        placeholder={t("settlements.detail.supplierNotePlaceholder")}
      />
    </>
  );
}

function RegisteredInvoiceCard({
  invoice,
  busy,
  submitting,
  onReplace,
  onViewInvoice,
  onDownloadInvoice,
}: {
  invoice: RegisteredSupplierInvoice;
  busy: boolean;
  submitting: boolean;
  onReplace: (data: ReplaceSupplierInvoiceInput) => Promise<unknown>;
  onViewInvoice?: (documentId: string) => void;
  onDownloadInvoice?: (documentId: string) => void;
}) {
  const { t } = useTranslation("payments");
  const { t: tCommon } = useTranslation("common");
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState<InvoiceFieldValues>({
    number: invoice.reference ?? "",
    date: "",
    file: null,
    note: "",
  });
  const canOpenDocuments = Boolean(onViewInvoice && onDownloadInvoice);

  const openReplace = () => {
    setFields({
      number: invoice.reference ?? "",
      date: "",
      file: null,
      note: "",
    });
    setEditing(true);
  };

  const handleReplaceSubmit = async () => {
    if (!requireNumberAndFile(fields, t)) return;
    try {
      await onReplace({
        documentId: invoice.documentId,
        supplierInvoiceNumber: fields.number.trim(),
        supplierInvoiceDate: fields.date || undefined,
        file: fields.file!,
        note: fields.note.trim() || undefined,
      });
      setEditing(false);
      setFields((prev) => ({ ...prev, file: null }));
    } catch {
      /* toast via mutation onError; keep the form open */
    }
  };

  return (
    <div className="border-surface-200 bg-surface-0 space-y-2.5 rounded-lg border px-4 py-3 shadow-xs">
      <p className="text-surface-900 font-mono text-sm font-medium">
        {invoice.reference ??
          t("settlements.detail.steps.supplierRegisteredUntitled")}
      </p>
      {invoice.invoicedAt ? (
        <p className="text-surface-500 text-xs">
          {t("settlements.detail.steps.supplierRegisteredAt", {
            when: formatDateTime(invoice.invoicedAt),
          })}
        </p>
      ) : null}
      {canOpenDocuments ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={busy}
            onClick={() => onViewInvoice?.(invoice.documentId)}
          >
            {t("settlements.detail.viewSupplierInvoice")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={busy}
            onClick={() => onDownloadInvoice?.(invoice.documentId)}
          >
            {t("settlements.detail.downloadSupplierInvoice")}
          </Button>
        </div>
      ) : null}
      {!editing ? (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={busy}
          onClick={openReplace}
        >
          {t("settlements.detail.steps.supplierReplaceButton")}
        </Button>
      ) : (
        <div className="border-surface-200 space-y-3 border-t pt-3">
          <p className="text-surface-500 text-xs">
            {t("settlements.detail.steps.supplierReplaceDesc")}
          </p>
          <SupplierInvoiceFields
            idPrefix={`replace-${invoice.documentId}`}
            values={fields}
            disabled={busy}
            onChange={(patch) => setFields((prev) => ({ ...prev, ...patch }))}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setFields((prev) => ({ ...prev, file: null }));
              }}
            >
              {tCommon("cancel")}
            </Button>
            <Button
              type="button"
              className="flex-1"
              disabled={busy}
              isLoading={submitting}
              onClick={() => void handleReplaceSubmit()}
            >
              {t("settlements.detail.steps.supplierReplaceSubmit")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function SupplierInvoiceStep({
  busy,
  noSelection,
  submitting,
  invoiceableCents,
  selectedLines,
  onRecord,
  onReplace,
  onViewInvoice,
  onDownloadInvoice,
  disabled = false,
  ...card
}: StepCardProps & {
  busy: boolean;
  noSelection: boolean;
  submitting: boolean;
  invoiceableCents: number;
  selectedLines: SettlementLineRow[];
  onRecord: (data: {
    supplierInvoiceNumber: string;
    supplierInvoiceDate?: string;
    totalCents: number;
    file: File;
    note?: string;
  }) => void | Promise<void>;
  onReplace: (data: ReplaceSupplierInvoiceInput) => Promise<unknown>;
  onViewInvoice?: (documentId: string) => void;
  onDownloadInvoice?: (documentId: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("payments");
  const [fields, setFields] = useState<InvoiceFieldValues>({
    number: "",
    date: "",
    file: null,
    note: "",
  });
  const [total, setTotal] = useState("");
  const formDisabled = disabled || busy || noSelection;

  const registered = useMemo(
    () => registeredInvoicesFromLines(selectedLines),
    [selectedLines],
  );
  const showRegistered = card.completed && registered.length > 0;

  const handleSubmit = async () => {
    if (!requireNumberAndFile(fields, t)) return;
    const totalCheck = checkSupplierInvoiceTotal(total, invoiceableCents);
    if (!totalCheck.ok) {
      toast.error(
        t(TOTAL_ERROR_KEYS[totalCheck.error], {
          expected: formatMoneyInput(invoiceableCents),
        }),
      );
      return;
    }
    await onRecord({
      supplierInvoiceNumber: fields.number.trim(),
      supplierInvoiceDate: fields.date || undefined,
      totalCents: totalCheck.totalCents,
      file: fields.file!,
      note: fields.note.trim() || undefined,
    });
  };

  return (
    <SettlementStepCard
      {...card}
      title={t("settlements.detail.steps.supplierTitle")}
      description={
        showRegistered
          ? t("settlements.detail.steps.supplierRegisteredDesc")
          : t("settlements.detail.steps.supplierDesc")
      }
      disabled={showRegistered ? busy || noSelection : formDisabled}
    >
      {showRegistered ? (
        <div className="space-y-3">
          {registered.map((invoice) => (
            <RegisteredInvoiceCard
              key={invoice.documentId}
              invoice={invoice}
              busy={busy}
              submitting={submitting}
              onReplace={onReplace}
              onViewInvoice={onViewInvoice}
              onDownloadInvoice={onDownloadInvoice}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <SupplierInvoiceFields
            idPrefix="supplier-invoice"
            values={fields}
            disabled={formDisabled}
            onChange={(patch) => setFields((prev) => ({ ...prev, ...patch }))}
            afterDate={
              <div>
                <Label
                  htmlFor="supplier-invoice-total"
                  size="sm"
                  spacing="tight"
                >
                  {t("settlements.detail.steps.supplierTotalLabel")}
                </Label>
                <Input
                  id="supplier-invoice-total"
                  inputMode="decimal"
                  value={total}
                  onChange={(e) => setTotal(e.target.value)}
                  disabled={formDisabled}
                  placeholder="0.00"
                />
                {invoiceableCents > 0 ? (
                  <p className="text-surface-500 mt-1 text-xs">
                    {t("settlements.detail.steps.supplierExpectedTotal", {
                      amount: formatMoneyInput(invoiceableCents),
                    })}
                  </p>
                ) : null}
              </div>
            }
          />
          <Button
            type="button"
            className="w-full"
            disabled={formDisabled}
            isLoading={submitting}
            onClick={() => void handleSubmit()}
          >
            {t("settlements.detail.steps.supplierButton")}
          </Button>
        </div>
      )}
    </SettlementStepCard>
  );
}
